import { Fragment, useMemo, type FC } from 'react';

import { ThrowError } from '#apps/client-web/app/components/throw-error';
import { routes } from '#apps/client-web/app/routes';

import { type AuditLightingCountFormEntry } from '#packages/data-collection/models/audit-lighting-count-form';
import { type FixtureType } from '#packages/data-collection/models/audit-lighting-type-form';
import { FixtureTypeLabel } from '#apps/client-web/features/audit-form-lighting-type/constants';
import { type ViewModelProps } from '#packages/web/types/view-model-props';
import { type LoggingContext } from '#packages/data-collection/types/logging-context';
import { EmptyPage, LoadingPage } from '#packages/web/layouts/page/templates';
import { Fab } from '#packages/web/foundations/buttons/fab';

import { type AuditFormLightingCountPageViewModel } from './audit-form-lighting-count-page.view-model';
import { AuditFormLightingCountPageRenderer } from './audit-form-lighting-count-page.renderer';

export type AuditFormLightingCountPageContainerProps =
  ViewModelProps<AuditFormLightingCountPageViewModel> & LoggingContext;

export const AuditFormLightingCountPageContainer: FC<
  AuditFormLightingCountPageContainerProps
> = ({ viewModel }) => {
  const { auditId, auditFormQuery, lightingFormQuery, onCreate } = viewModel;

  // Build id → human-readable label map from the lighting type entries
  const fixtureTypeLabelById = useMemo<Map<string, string>>(() => {
    const map = new Map<string, string>();
    for (const entry of lightingFormQuery.data?.entries ?? []) {
      if (entry.id === undefined) continue;
      const label =
        entry.luminaireName ??
        (entry.fixtureType
          ? FixtureTypeLabel[entry.fixtureType as FixtureType]
          : undefined);
      if (label) map.set(entry.id, label);
    }
    return map;
  }, [lightingFormQuery.data?.entries]);

  const groups = useMemo(() => {
    const entries = auditFormQuery.data?.entries ?? [];

    // Group by section; entries without a section go into 'unspecified'
    const grouped: Record<
      string,
      { entry: AuditLightingCountFormEntry; globalIndex: number }[]
    > = {};
    entries.forEach((entry, globalIndex) => {
      const key = entry.section ?? 'unspecified';
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push({ entry, globalIndex });
    });

    return Object.entries(grouped).map(([section, groupEntries]) => ({
      id: section,
      label: section === 'unspecified' ? 'Unspecified' : section,
      entries: groupEntries.map(({ entry, globalIndex }) => ({
        link: routes.audit.getEnergyAuditLightingCountFormEntryPath(
          auditId!,
          globalIndex,
        ),
        entry,
        fixtureTypeLabel:
          entry.fixtureType !== undefined
            ? fixtureTypeLabelById.get(entry.fixtureType)
            : undefined,
      })),
    }));
  }, [auditFormQuery.data, auditId, fixtureTypeLabelById]);

  const isLoading = auditFormQuery.isLoading;
  const isEmpty = auditFormQuery.isSuccess && groups.length < 1;
  const hasData = !isLoading && !isEmpty;

  return (
    <Fragment>
      {isLoading && <LoadingPage />}
      {isEmpty && <EmptyPage onActionButtonClick={onCreate} />}
      {hasData && <AuditFormLightingCountPageRenderer groups={groups} />}
      {hasData && <Fab onClick={onCreate} />}
      {auditFormQuery.isError && <ThrowError error={auditFormQuery.error} />}
    </Fragment>
  );
};
